<template>
  <div class="main content">
    <h1>Your Legacy Library</h1>
    <div class="container py-4">
      <div class="row">
        <div
          v-for="program in combine8WeeksWithCoaching"
          :key="program.id"
          class="col-12 col-sm-6 col-md-4 program my-4"
          :class="{ locked: isLocked(program) }"
          @click="next(program)"
        >
          <h2
            class="text-center mb-2"
            style="height: auto; min-height: 40px;"
          >
            {{ program.title }}
          </h2>
          <!-- Popover only on web; touch devices get sticky hover tooltip -->
          <div
            v-if="!isNative"
            v-b-popover.hover.top="getPopoverText(program)"
            class="img-container"
            :class="{
              blocked: isBlocked(program),
              locked: isLocked(program),
              'client-user': userData.client,
            }"
          >
            <program-logo
              :blocked="isBlocked(program)"
              :title="program.title"
              :image="program.picture"
            />

            <div
              v-if="isLocked(program)"
              class="locked-overlay text-center"
            >
              <p
                class="mb-0 font-weight-bold text-white"
                style="text-shadow: 1px 1px 4px black;"
              >
                Starts {{ getLockedDate(program) }}
              </p>
            </div>
          </div>
          <div
            v-else
            class="img-container"
            :class="{
              blocked: isBlocked(program),
              locked: isLocked(program),
              'client-user': userData.client,
            }"
          >
            <program-logo
              :blocked="isBlocked(program)"
              :title="program.title"
              :image="program.picture"
            />

            <div
              v-if="isLocked(program)"
              class="locked-overlay text-center"
            >
              <p
                class="mb-0 font-weight-bold text-white"
                style="text-shadow: 1px 1px 4px black;"
              >
                Starts {{ getLockedDate(program) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';
import ProgramLogo from '@/components/ProgramLogo.vue';
import { isStoreApp } from '@/utils/platform';

export default {
  name: 'Profile',
  components: {
    ProgramLogo,
  },
  computed: {
    isNative() {
      try {
        return Capacitor.getPlatform() !== 'web';
      } catch {
        return false;
      }
    },
    userData() {
      return this.$store.state.userData;
    },
    programs() {
      return this.$store.state.programs;
    },
    combine8WeeksWithCoaching() {
      try {
        const programsList = Array.isArray(this.programs) ? this.programs : [];

        // Sort so enrolled programs appear first
        return programsList.sort((a, b) => {
          const aEnrolled = a.purchased || (a.active_enrollment && a.active_enrollment.status === 'active');
          const bEnrolled = b.purchased || (b.active_enrollment && b.active_enrollment.status === 'active');

          // Enrolled programs first
          if (aEnrolled && !bEnrolled) return -1;
          if (!aEnrolled && bEnrolled) return 1;

          // Then by ordering field
          return (a.ordering || 0) - (b.ordering || 0);
        });
      } catch {
        return [];
      }
    },
  },
  methods: {
    isLocked(program) {
      // It is locked IF:
      // 1. It has an enrollment
      // 2. That enrollment has a Wave
      // 3. The Wave start date is in the future
      if (program.active_enrollment && program.active_enrollment.wave) {
        const start = new Date(`${program.active_enrollment.wave.start_date}T00:00:00`);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset today's time to midnight for fair comparison

        return start > today;
      }
      return false;
    },
    isBlocked(program) {
      // Block visual if: Not purchased OR No active enrollment
      // Purchased programs stay blue even if wave hasn't started yet
      return !program.purchased || !program.active_enrollment;
    },
    getLockedDate(program) {
      if (program.active_enrollment && program.active_enrollment.wave) {
        const date = new Date(`${program.active_enrollment.wave.start_date}T00:00:00`);
        return date.toLocaleDateString();
      }
      return '';
    },
    getPopoverText(program) {
      if (this.userData.client) return '';

      if (this.isLocked(program)) {
        return `Class begins on ${this.getLockedDate(program)}`;
      }

      if (!program.purchased) {
        return 'Click To Learn More';
      }

      if (!program.active_enrollment) {
        return 'End of Access, Click to Learn More';
      }

      return '';
    },
    next(program) {
      // 1. Check Lock First
      if (this.isLocked(program)) {
        return; // Do nothing (the popover explains why)
      }

      // 2. Normal Navigation
      if (program.active_enrollment) {
        if (program.title === 'Living In Excellence') {
          this.$router.push({ name: 'living_in_excellence' });
        } else {
          this.$router.push({ name: 'program', params: { id: program.active_enrollment.id } });
        }
      } else if (isStoreApp()) {
        this.$router.push({ name: 'ios_membership' });
      } else if (this.userData.client) {
        // Blocked program - client user - go to client-specific enroll page
        this.$router.push({ name: 'client-enroll_new', params: { clientSlug: this.userData.client.slug } });
      } else {
        // Blocked program - non-client user - go to regular enroll page
        this.$router.push({ name: 'enroll_new' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/breakpoints.scss';

@media #{$phone-landscape} {
  h1 {
    margin: 0.5rem 0;
    font-size: 1.25rem;
  }

  .container {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }

  .program {
    margin-top: 0.5rem !important;
    margin-bottom: 0.5rem !important;

    h2 {
      font-size: 0.95rem;
      margin-bottom: 0.5rem !important;
      min-height: auto !important;
    }
  }
}

.program {
  cursor: pointer;

  &.locked {
    cursor: default;
  }

  .img-container {
    width: 100%;
    border-radius: 50%;
    border: 2px solid transparent;
    position: relative;
    overflow: hidden;

    &.locked {
      cursor: default;
    }
 }

  @media (hover: hover) {
    .img-container:hover:not(.locked) {
      border: 2px solid var(--primary);
      box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.15);
      transition: border 100ms ease-out, box-shadow 150ms ease-out;
      width: 100%;
      color: blue;
    }

    .blocked:hover:not(.locked) {
      border: 2px solid var(--primary);
      box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.15);
      cursor: pointer;
    }
  }
}
.blocked {
  filter: brightness(90%);
  cursor: pointer;
}
.blocked.client-user {
  cursor: pointer;
}
h2 {
  font-weight: bold;
  margin-bottom: 30px;
}

.locked-overlay {
  position: absolute;
  bottom: 20%;
  left: 0;
  width: 100%;
  z-index: 10;
  pointer-events: none;
}
</style>
